import {
  CSSProperties, FunctionComponent, MutableRefObject, ReactNode, forwardRef, useEffect, useRef,
} from 'react';
import { positionValues, Scrollbars } from 'react-custom-scrollbars';

import { DeviceInfo } from '@ezugi/utils';

import {
  Container, hThumb, vThumb, hTrack, vTrack, hThumbStyle, vThumbStyle, hTrackStyle, vTrackStyle,
} from './style';

const renderEl = (
  {
    style,
    ...props
  }: any,
  className: string,
  styleName: CSSProperties
) => (
  <div {...props} style={{ ...style, ...styleName }} className={className} />
);

const renderHThumb = (props: FunctionComponent<any>) => renderEl(props, hThumb, hThumbStyle);
const renderVThumb = (props: FunctionComponent<any>) => renderEl(props, vThumb, vThumbStyle);
const renderHTrack = (props: FunctionComponent<any>) => renderEl(props, hTrack, hTrackStyle);
const renderVTrack = (props: FunctionComponent<any>) => renderEl(props, vTrack, vTrackStyle);

const defaultMobileProps = {
  autoHide: true,
  autoHideTimeout: 1000,
  autoHideDuration: 270,
};

export type ScrollablePositionValues = positionValues

type ScrollableProps = {
  useNativeOnMobile?: boolean;
  autoHide?: boolean;
  autoHideTimeout?: number;
  autoHideDuration?: number;
  updateOnChildrenChange?: boolean;
  hideTracksWhenNotNeeded?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  disableHorizontal?: boolean;
  onUpdate?: ((values: positionValues) => void) | undefined;
}

const Scrollable = forwardRef(({
  useNativeOnMobile = true,
  updateOnChildrenChange = false,
  hideTracksWhenNotNeeded = true,
  disableHorizontal = false,
  ...props
}: ScrollableProps, ref) => {
  const localRef = useRef();
  const scrollRef = ref as MutableRefObject<any> ?? localRef;
  const nativeMobileProps = { ...defaultMobileProps, hideTracksWhenNotNeeded, ...props };
  const containedScrollbarsProps = { autoHide: false, hideTracksWhenNotNeeded, ...props };

  // Workaround for the following issue https://github.com/malte-wessel/react-custom-scrollbars/issues/240
  useEffect(() => {
    if (!scrollRef.current?.view || !updateOnChildrenChange || DeviceInfo.isHandheld) return undefined;

    const targetNode = scrollRef.current.view;
    const config = { childList: true };

    const callback = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          scrollRef.current.update.call(scrollRef.current);
        }
      }
    };

    const observer = new MutationObserver(callback);

    observer.observe(targetNode, config);

    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    useNativeOnMobile && DeviceInfo.isHandheld ? (
      <Scrollbars ref={scrollRef} {...nativeMobileProps} />
    ) : (
      <Container>
        <Scrollbars
          ref={scrollRef}
          renderThumbHorizontal={renderHThumb}
          renderThumbVertical={renderVThumb}
          renderTrackHorizontal={renderHTrack}
          renderTrackVertical={renderVTrack}
          renderView={disableHorizontal ? (_props: any) => (
            <div {..._props} style={{ ..._props.style, overflowX: 'hidden' }} />
          ) : undefined}
          {...containedScrollbarsProps}
        />
      </Container>
    ));
});

export default Scrollable;
